// frontend/src/scenes/admin/Departments.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import AdminBackButton from "./AdminBackButton";
import { useNavigate } from "react-router-dom";
import { authDelete, authGet, authPost, getUser } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.orgs)) return resp.orgs;
  if (Array.isArray(resp.organizations)) return resp.organizations;
  return [];
}

const DEFAULT_DEPARTMENT_FORM = {
  code: "",
  name: "",
  description: "",
  is_active: true,
  csv_enabled_requested: false,
  machine_enabled_requested: false,
  negative_label: "Negative",
  positive_label: "Positive",
  default_result: "Negative",
  positive_result: "Positive",
  default_flag: "Negative",
  positive_flag: "Positive",
};

export default function AdminDepartments() {
  const navigate = useNavigate();
  const initialOrgId = new URLSearchParams(window.location.search).get("org") || "";
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [departmentForm, setDepartmentForm] = useState(DEFAULT_DEPARTMENT_FORM);
  const [analytes, setAnalytes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
  const canWrite = ["owner", "global_admin", "superadmin", "admin"].includes(role);
  const selectedOrg = useMemo(() => orgs.find((r) => r.id === selectedOrgId) || null, [orgs, selectedOrgId]);
  const selectedDepartment = useMemo(() => departments.find((d) => d.id === selectedDepartmentId) || null, [departments, selectedDepartmentId]);

  const loadOrgs = async () => {
    setBusy(true);
    setErr("");
    try {
      const j = await authGet("/api/admin/organizations");
      const nextRows = pickRows(j);
      setOrgs(nextRows);
      if (nextRows.length) {
        const queryOrg = initialOrgId && nextRows.some((r) => r.id === initialOrgId) ? initialOrgId : "";
        if (!selectedOrgId || (queryOrg && selectedOrgId !== queryOrg)) setSelectedOrgId(queryOrg || nextRows[0].id);
      }
    } catch (e) {
      setErr(e.message || "Failed to load organizations.");
      setOrgs([]);
    } finally {
      setBusy(false);
    }
  };

  const loadDepartments = async (orgId = selectedOrgId) => {
    if (!orgId) return;
    setBusy(true);
    setErr("");
    try {
      const resp = await authGet(`/api/admin/organizations/${encodeURIComponent(orgId)}/departments`);
      const deptRows = pickRows(resp);
      setDepartments(deptRows);
      if (!deptRows.some((d) => d.id === selectedDepartmentId)) {
        setSelectedDepartmentId(deptRows[0]?.id || "");
        setDepartmentForm(deptRows[0] ? { ...DEFAULT_DEPARTMENT_FORM, ...deptRows[0] } : DEFAULT_DEPARTMENT_FORM);
      }
    } catch (e) {
      setErr(e.message || "Failed to load departments.");
      setDepartments([]);
      setSelectedDepartmentId("");
      setAnalytes([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrgId) loadDepartments(selectedOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const addDepartment = async () => {
    if (!selectedOrgId) return;
    setBusy(true);
    setErr("");
    try {
      if (!departmentForm.name.trim()) throw new Error("Department name is required.");
      if (!departmentForm.code.trim()) throw new Error("Department code is required.");
      const resp = await authPost(`/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/departments`, departmentForm);
      setDepartmentForm(DEFAULT_DEPARTMENT_FORM);
      setToast({ open: true, msg: "Department saved. Manual reporting is active immediately.", severity: "success" });
      await loadDepartments(selectedOrgId);
      if (resp?.row?.id) setSelectedDepartmentId(resp.row.id);
    } catch (e) {
      setErr(e.message || "Failed to save department.");
    } finally {
      setBusy(false);
    }
  };

  const loadAnalytes = async (departmentId = selectedDepartmentId) => {
    if (!selectedOrgId || !departmentId) return;
    try {
      const resp = await authGet(`/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/departments/${encodeURIComponent(departmentId)}/analytes`);
      setAnalytes(pickRows(resp));
    } catch (e) {
      setAnalytes([]);
      setErr(e.message || "Failed to load analytes.");
    }
  };

  useEffect(() => {
    if (selectedDepartmentId) loadAnalytes(selectedDepartmentId);
    else setAnalytes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentId]);

  const openAnalyteManager = (department = selectedDepartment) => {
    if (!selectedOrgId || !department?.id) return;
    navigate(`/analytes?org=${encodeURIComponent(selectedOrgId)}&department=${encodeURIComponent(department.id)}`);
  };

  const removeDepartment = async (department = selectedDepartment) => {
    if (!canWrite || !selectedOrgId || !department?.id) return;
    const ok = window.confirm(`Remove department ${department.code} — ${department.name}? This also removes its analytes and hides it from LIS entry.`);
    if (!ok) return;
    setBusy(true);
    setErr("");
    try {
      await authDelete(`/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/departments/${encodeURIComponent(department.id)}`);
      setToast({ open: true, msg: "Department removed.", severity: "success" });
      if (selectedDepartmentId === department.id) {
        setSelectedDepartmentId("");
        setDepartmentForm(DEFAULT_DEPARTMENT_FORM);
        setAnalytes([]);
      }
      await loadDepartments(selectedOrgId);
    } catch (e) {
      setErr(e.message || "Failed to remove department.");
    } finally {
      setBusy(false);
    }
  };

  const departmentColumns = useMemo(
    () => [
      { field: "code", headerName: "Code", width: 120 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
      { field: "description", headerName: "Description", flex: 1, minWidth: 240 },
      { field: "is_active", headerName: "Active", width: 100, type: "boolean" },
      {
        field: "actions",
        headerName: "Actions",
        width: 210,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={1}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); openAnalyteManager(params.row); }}>
              Analytes
            </Button>
            {canWrite ? (
              <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); removeDepartment(params.row); }}>
                Remove
              </Button>
            ) : null}
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, selectedOrgId, selectedDepartmentId, selectedDepartment]
  );

  return (
    <Box sx={{ flex: "1 1 auto", minHeight: 0, minWidth: 0, height: "100%", overflow: "auto", p: { xs: 1, md: 2 }, pb: 4 }}>
      <AdminBackButton />
      <Header title="Departments" subtitle="Set up lab departments and default result labels; manage analytes from the connected Analytes page" />

      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Organization</InputLabel>
                <Select label="Organization" value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
                  {orgs.map((org) => <MenuItem key={org.id} value={org.id}>{org.name}{org.code ? ` (${org.code})` : ""}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Departments are configured independently from the Organization page. Add only the departments this lab actually runs.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedOrg ? (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Guided Department Setup</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Manual reporting is enabled immediately; CSV upload and machine interfaces stay in pending/escalated status until configured.
                </Alert>
                {!canWrite ? <Alert severity="warning" sx={{ mb: 2 }}>Your role can read this configuration, but only Admin or Global Admin roles can make changes.</Alert> : null}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <TextField label="Code" fullWidth size="small" value={departmentForm.code} onChange={(e) => setDepartmentForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Name" fullWidth size="small" value={departmentForm.name} onChange={(e) => setDepartmentForm((s) => ({ ...s, name: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField label="Description" fullWidth size="small" value={departmentForm.description || ""} onChange={(e) => setDepartmentForm((s) => ({ ...s, description: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Negative / Normal Label" fullWidth size="small" value={departmentForm.negative_label || ""} onChange={(e) => setDepartmentForm((s) => ({ ...s, negative_label: e.target.value, default_result: e.target.value, default_flag: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Positive / Abnormal Label" fullWidth size="small" value={departmentForm.positive_label || ""} onChange={(e) => setDepartmentForm((s) => ({ ...s, positive_label: e.target.value, positive_result: e.target.value, positive_flag: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox checked={departmentForm.is_active !== false} onChange={(e) => setDepartmentForm((s) => ({ ...s, is_active: e.target.checked }))} />} label="Active" />
                    <FormControlLabel control={<Checkbox checked disabled />} label="Manual reporting active" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel control={<Checkbox checked={!!departmentForm.csv_enabled_requested} onChange={(e) => setDepartmentForm((s) => ({ ...s, csv_enabled_requested: e.target.checked }))} />} label="Needs CSV upload/mapping" />
                    <FormControlLabel control={<Checkbox checked={!!departmentForm.machine_enabled_requested} onChange={(e) => setDepartmentForm((s) => ({ ...s, machine_enabled_requested: e.target.checked }))} />} label="Needs machine integration escalation" />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={addDepartment} disabled={busy || !canWrite}>Add / Update Department</Button>
                    <Button variant="text" onClick={() => setDepartmentForm(DEFAULT_DEPARTMENT_FORM)} disabled={busy || !canWrite} sx={{ ml: 1 }}>Clear</Button>
                  </Grid>
                </Grid>
                <Box sx={{ height: 330, width: "100%" }}>
                  <DataGrid
                    rows={departments}
                    columns={departmentColumns}
                    loading={busy}
                    getRowId={(r) => r.id}
                    rowSelectionModel={selectedDepartmentId ? [selectedDepartmentId] : []}
                    onRowClick={(params) => {
                      setSelectedDepartmentId(params.row.id);
                      setDepartmentForm({ ...DEFAULT_DEPARTMENT_FORM, ...params.row });
                    }}
                    disableRowSelectionOnClick={false}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Analytes Managed Separately</Typography>
                {selectedDepartment ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>{selectedDepartment.code}</strong> — {selectedDepartment.name}</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip size="small" color="success" label="Manual active" sx={{ mr: 1, mb: 1 }} />
                      <Chip size="small" color={selectedDepartment.csv_enabled_requested ? "warning" : "default"} label={`CSV: ${selectedDepartment.csv_status || "not_requested"}`} sx={{ mr: 1, mb: 1 }} />
                      <Chip size="small" color={selectedDepartment.machine_enabled_requested ? "info" : "default"} label={`Machine: ${selectedDepartment.machine_status || "not_requested"}`} sx={{ mr: 1, mb: 1 }} />
                    </Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Departments define the lab section and result labels. Add, update, import, or remove analytes from the Analytes page so all analyte work happens in one place.
                    </Alert>
                    <Button variant="contained" onClick={() => openAnalyteManager()} disabled={busy || !selectedOrgId || !selectedDepartmentId} sx={{ mr: 1, mb: 1 }}>
                      Open Analyte Manager
                    </Button>
                    {canWrite ? <Button variant="outlined" color="error" onClick={() => removeDepartment()} disabled={busy} sx={{ mb: 1 }}>Remove Department</Button> : null}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Current analytes for this department are shown below for review only. Use Open Analyte Manager for manual add, template download, CSV upload, edits, and deletes.
                    </Typography>
                    <Box sx={{ height: 300, width: "100%" }}>
                      <DataGrid
                        rows={analytes}
                        columns={[
                          { field: "analyte_code", headerName: "Code", width: 130 },
                          { field: "analyte_name", headerName: "Analyte", flex: 1, minWidth: 170 },
                          { field: "result_type", headerName: "Type", width: 110 },
                          { field: "is_active", headerName: "Active", width: 90, type: "boolean" },
                        ]}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                      />
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">Select or create a department, then open the Analyte Manager to add or upload analytes.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Create an organization first, then return here to configure departments.</Alert>
      )}

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
